<template>
  <section class="blog-feed">
    <div class="container">
      <h2>{{ $t('blog.title') }}</h2>
      <div class="feed-header">
        <div class="search-container">
          <input 
            type="text" 
            v-model="searchQuery" 
            :placeholder="$t('blog.searchPlaceholder')"
            @keyup.enter="handleSearch"
            aria-label="Context-aware search"
          />
          <button @click="handleSearch" :disabled="agentLoading" aria-label="Search blog posts with AI">{{ $t('blog.askButton') }}</button>
        </div>
      </div>

      <div v-if="agentLoading" class="agent-loading-overlay" role="status" aria-live="polite">
        <div class="spinner"></div>
        <p>{{ $t('blog.loading') }}</p>
      </div>

      <!-- Agent Response Area -->
      <div v-if="agentResponse" class="agent-response-card" role="region" aria-label="AI search response" aria-live="polite">
        <div class="agent-header">
          <span class="agent-icon">🤖</span>
          <h3>{{ $t('blog.agentTitle') }}</h3>
          <button @click="agentResponse = null" class="close-btn" aria-label="Close AI response">×</button>
        </div>
        <div class="agent-content">
          <p>{{ agentResponse.text }}</p>
          <div v-if="agentResponse.relevantPost" class="relevant-post-link">
            <strong>{{ $t('blog.readMore') }}:</strong>
            <a :href="agentResponse.relevantPost.link" target="_blank">{{ agentResponse.relevantPost.title }}</a>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading">{{ $t('blog.loading') }}</div>
      <div v-else class="posts">
        <div v-for="post in filteredPosts" :key="post.id" class="post-card">
          <h3>{{ post.title }}</h3>
          <p>{{ post.excerpt }}</p>
          <div class="tags">
            <span v-for="tag in post.tags" :key="tag" class="tag">#{{ tag }}</span>
          </div>
          <a :href="post.link" target="_blank" class="read-more">{{ $t('blog.readMore') }} →</a>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { db } from '@/firebase';
import { collection, query, where, getDocs, vector, VectorValue } from 'firebase/firestore';
import * as use from '@tensorflow-models/universal-sentence-encoder';
import * as tf from '@tensorflow/tfjs';
import '@tensorflow/tfjs-backend-cpu';
import logger from '@/utils/logger';

const { t } = useI18n();

const searchQuery = ref('');
const loading = ref(true);
const agentLoading = ref(false);
const agentResponse = ref(null);
const posts = ref([]);
const model = ref(null);

const stripHtml = (html) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const fetchPosts = async () => {
  logger.info('Fetching posts...');
  try {
    loading.value = true;
    const response = await fetch('https://blog.data-do.de/wp-json/wp/v2/posts?per_page=3&_embed');
    logger.info('Fetch response status:', { status: response.status });
    if (!response.ok) throw new Error('Failed to fetch posts');
    const data = await response.json();
    logger.info('Posts fetched:', { count: data.length });
    
    posts.value = data.map(post => ({
      id: post.id,
      title: stripHtml(post.title.rendered),
      excerpt: stripHtml(post.excerpt.rendered).split(' ').slice(0, 25).join(' ') + '...',
      link: post.link,
      tags: post._embedded && post._embedded['wp:term'] 
        ? post._embedded['wp:term'].flat().filter(t => t.taxonomy === 'post_tag').map(t => t.name)
        : []
    }));
  } catch (error) {
    logger.error('Error fetching blog posts:', error);
    // Fallback to minimal info if fetch fails
  } finally {
    loading.value = false;
  }
};

/**
 * Safe initialization for TensorFlow.js and Universal Sentence Encoder.
 * Ensures the engine is patched against the "undefined backend" bug
 * before the model is allowed to load tensors into memory.
 */
const initializeModel = async (forceRebuild = false) => {
  if (model.value && !forceRebuild) return model.value;

  try {
    // 1. Completely clear the global TF state
    tf.engine().reset();

    // 2. Explicitly register the backend if it's missing from the registry
    if (!tf.findBackend('cpu')) {
      logger.warn('CPU Backend not found in registry, re-registering...');
      // This is usually handled by the import, but sometimes tree-shaking breaks it
    }

    await tf.setBackend('cpu');
    await tf.ready();

    // 3. APPLY THE PATCH TO THE NEW ENGINE INSTANCE
    const engine = tf.engine();
    const patch = (dataId) => {
      const info = engine.state.tensorInfo.get(dataId);
      if (info && !info.backend) info.backend = engine.backend;
    };

    engine.readSync = (original => (dataId) => {
      patch(dataId);
      return original.call(engine, dataId);
    })(engine.readSync);

    engine.read = (original => async (dataId) => {
      patch(dataId);
      return await original.call(engine, dataId);
    })(engine.read);

    // 4. Load model - use the "lite" version if memory/backend issues persist
    // use.load() accepts a config object. Setting it explicitly can help.
    model.value = await use.load();

    logger.info('USE Model loaded on clean engine');
    return model.value;
  } catch (error) {
    logger.error('Hard Reset failed', { message: error.message });
    throw error;
  }
};

onMounted(async () => {
  fetchPosts();
  try {
    await initializeModel();
  } catch (e) {
    // Error is already logged in initializeModel
  }
});

const handleSearch = async () => {
  if (!searchQuery.value) return;

  agentLoading.value = true;
  agentResponse.value = null;

  try {
    const terms = searchQuery.value.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    const kbRef = collection(db, 'knowledge_base');

    // --- STEP 1: IMMEDIATE KEYWORD SEARCH ---
    // This runs instantly and doesn't need TensorFlow
    let initialResult = null;
    if (terms.length > 0) {
      const kQuery = query(kbRef, where('keywords', 'array-contains-any', terms.slice(0, 10)));
      const kSnapshot = await getDocs(kQuery);

      if (!kSnapshot.empty) {
        const match = kSnapshot.docs[0].data();
        initialResult = {
          text: match.answer,
          relevantPost: posts.value.find(p => p.id === match.postId) || null,
          isAiOptimized: false
        };
        // Show the keyword result immediately
        agentResponse.value = initialResult;
      }
    }

    // --- STEP 2: BACKGROUND VECTOR SEARCH (THE "UPGRADE") ---
    try {
      await initializeModel(); // Your patched init function

      let embeddings = await model.value.embed([searchQuery.value]);
      const rawData = await embeddings.array();
      const embeddingArray = rawData[0];
      embeddings.dispose();

      const vQuery = query(
          kbRef,
          vector('embedding', VectorValue.vector(embeddingArray), {
            limit: 1,
            distanceMeasure: 'COSINE'
          })
      );
      const vSnapshot = await getDocs(vQuery);

      if (!vSnapshot.empty) {
        const bestMatch = vSnapshot.docs[0].data();
        // Replace keyword result with the "smarter" AI result
        agentResponse.value = {
          text: bestMatch.answer,
          relevantPost: posts.value.find(p => p.id === bestMatch.postId) || null,
          isAiOptimized: true
        };
      }
    } catch (aiError) {
      // If AI fails, we just log it. The user already has the keyword result!
      logger.warn('AI Upgrade failed, staying with keyword results', { message: aiError.message });

      if (!agentResponse.value) {
        agentResponse.value = { text: t('blog.noResult'), relevantPost: null };
      }
    }

  } catch (error) {
    logger.error('Critical search error', { message: error.message });
  } finally {
    // ALWAYS stop the spinner
    agentLoading.value = false;
  }
};

const filteredPosts = computed(() => posts.value);
</script>

<style scoped>
.blog-feed {
  padding: 80px 20px;
  background-color: var(--white);
}

.container {
  max-width: 1100px;
  margin: 0 auto;
}

h2 {
  text-align: center;
  margin-bottom: 30px;
  color: var(--text-color);
}

.feed-header {
  margin-bottom: 40px;
  display: flex;
  justify-content: center;
}

.agent-loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  gap: 10px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--gray-light);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.agent-response-card {
  max-width: 600px;
  margin: 0 auto 40px;
  background-color: var(--primary-light);
  border-left: 5px solid var(--primary-color);
  padding: 20px;
  border-radius: 8px;
  position: relative;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.agent-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.agent-icon {
  font-size: 1.5rem;
}

.agent-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--primary-dark);
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #888;
  padding: 0;
  width: auto;
}

.close-btn:hover {
  color: #333;
  background: none;
}

.agent-content p {
  margin-bottom: 10px;
  font-size: 1rem;
  color: var(--text-color);
  line-height: 1.5;
}

.relevant-post-link {
  font-size: 0.9rem;
}

.relevant-post-link a {
  color: var(--primary-color);
  text-decoration: underline;
  margin-left: 5px;
}

.search-container {
  display: flex;
  width: 100%;
  max-width: 600px;
  gap: 10px;
}

input {
  flex-grow: 1;
  padding: 12px 20px;
  border: 2px solid var(--gray-light);
  border-radius: 25px;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.3s;
}

input:focus {
  border-color: var(--primary-color);
}

button {
  padding: 10px 25px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.3s;
}

button:hover {
  background-color: var(--primary-dark);
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.posts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.post-card {
  padding: 25px;
  border: 1px solid var(--gray-light);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.3s;
}

.post-card:hover {
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}

h3 {
  margin-bottom: 15px;
  font-size: 1.25rem;
  color: var(--text-color);
}

p {
  color: var(--text-color);
  opacity: 0.8;
  margin-bottom: 20px;
  flex-grow: 1;
}

.tags {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.tag {
  color: var(--accent-dark);
  font-size: 0.85rem;
  font-weight: bold;
}

.read-more {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: bold;
  font-size: 0.9rem;
}

.read-more:hover {
  text-decoration: underline;
}

.loading {
  text-align: center;
  font-style: italic;
  color: #888;
}

@media (max-width: 768px) {
  .blog-feed {
    padding: 40px 15px;
  }
  .search-container {
    flex-direction: column;
    max-width: 100%;
  }
  .search-container input {
    width: 100%;
  }
  .search-container button {
    width: 100%;
  }
  .posts {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .agent-response-card {
    max-width: 100%;
  }
  h2 {
    font-size: 1.8rem;
  }
}
</style>
